<template>
  <v-card>
    <v-toolbar dark color="primary" tile flat class="mb-5">
      <v-btn icon dark @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>欠席登録</v-toolbar-title>
    </v-toolbar>

    <v-form ref="post_absence_form">
      <v-card-text class="py-3">
        <v-menu
          v-model="isDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="dateRangeText"
              label="期間"
              prepend-icon="mdi-calendar"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
              readonly
              v-bind="props"
              color="primary"
            ></v-text-field>
          </template>

          <!-- Vuetify 3ではピッカーとボタンをまとめるために v-card 等でラップします -->
          <v-card>
            <v-date-picker
              v-model="dates"
              hide-header
              multiple="range"
              color="primary"
              locale="ja"
              style="transform: scale(0.8);"
            ></v-date-picker>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" color="primary" @click="cancelDatePick">
                キャンセル
              </v-btn>
              <!-- $refs.save は廃止されたため、単にメニューを閉じます (値はv-modelで即時反映されます) -->
              <v-btn variant="text" color="primary" @click="isDatePicker = false">
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-select
          v-model="reason"
          :rules="[rules.required]"
          label="理由"
          :items="choices"
          item-title="text"
          item-value="value"
          outlined
          dense
          color="primary"
        />

        <v-select
          v-model="infectionReason"
          :rules="[infectionRules]"
          v-if="reason === 'infection'"
          label="詳細"
          :items="infectionChoices"
          item-title="text"
          item-value="value"
          outlined
          dense
          color="primary"
        />

        <v-textarea
          v-model="comment"
          v-if="reason === 'others'"
          :rules="[rules.counter500]"
          label="コメント"
          auto-grow
          outlined
          dense
          color="primary"
        />
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close" color="error" text>
          キャンセル
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="clickPost" :loading="btnLoading" color="primary" variant="flat">
          送信
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-form>
  </v-card>
</template>
<script lang="ts">
import Vue, { PropType, defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import rules from '@/service/ValidRules';

import { chatModule } from '@/store/dataModules/chatModule';
import { AbsenceReason, AbsenceReasonType } from 'chaild-api/lib/index';

// model
import { VForm } from '@/model/Vuetify/VForm';
import LocalDataService from '@/service/LocalDataService';

interface DataType {
  dates: string[];
  startedAt: string;
  endedAt: string;
  reason: string;
  infectionReason: string;
  comment: string | null;
  fetchLoading: boolean;
  isDatePicker: boolean;
  btnLoading: boolean;
  choices: any;
  infectionChoices: any;
}

export default defineComponent({
  name: 'AbsenceModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    childId: {
      type: Number,
      default: NaN,
    },
    date: {
      type: Object as PropType<any>,
      default: {},
    },
    nurseryId: {
      type: Number,
      default: NaN,
    },
  },

  computed: {
    rules() {
      return rules;
    },
    form(): VForm {
      return (this.$refs as any).post_absence_form;
    },
    allowMinDate(): string {
      return dayjs().format('YYYY-MM-DD');
    },
    dateRangeText() {
      if (!this.dates || this.dates.length === 0) {
        console.log(`dates: ${this.dates}`)
        return '';
      }
      // v-date-picker からの値を必ず YYYY-MM-DD で表示する
      console.log(`dates: ${this.dates}`)
      const rangeFirstDate = this.dates[0]
      const rangeLastDate = this.dates.slice(-1)[0]; 
      const rangeDates = [rangeFirstDate,rangeLastDate]
      const formatted = rangeDates.map((d: string) => dayjs(d).format('YYYY-MM-DD'));
      return formatted.join(' ~ ');
    },
    infectionRules() {
      if (this.reason === 'infection' && !this.infectionReason) {
        return '必須入力項目です';
      } else {
        return true;
      }
    },
  },

  watch: {
    isOpen: {
      async handler(val: boolean) {
        if (val) {
          this.dates = [];
          this.startedAt = '';
          this.endedAt = '';
          this.reason = '';
          this.comment = null;
        }
      },
      deep: true,
    },
  },

  data: (): DataType => ({
    dates: [],
    startedAt: '',
    endedAt: '',
    reason: '',
    infectionReason: '',
    comment: '',
    fetchLoading: false,
    isDatePicker: false,
    btnLoading: false,
    choices: [
      {
        text: '発熱',
        value: AbsenceReason.fever,
      },
      {
        text: '家庭の事情',
        value: AbsenceReason.personal,
      },
      {
        text: '感染症',
        value: 'infection',
      },
      {
        text: 'その他（コメントの入力が必要）',
        value: AbsenceReason.others,
      },
    ],
    infectionChoices: [
      {
        text: '新型コロナウイルス',
        value: AbsenceReason.covid19,
      },
      {
        text: '溶連菌感染症',
        value: AbsenceReason.infStrep,
      },
      {
        text: 'マイコプラズマ肺炎',
        value: AbsenceReason.infMycoplasma,
      },
      {
        text: '手足口病',
        value: AbsenceReason.infHFMD,
      },
      {
        text: '伝染性紅斑（りんご病）',
        value: AbsenceReason.infSlappedCheek,
      },
      {
        text: 'ウイルス性胃腸炎（ノロ、ロタ、アデノ等）',
        value: AbsenceReason.infStomach,
      },
      {
        text: 'ヘルパンギーナ',
        value: AbsenceReason.infHerpangina,
      },
      {
        text: 'RSウィルス感染症',
        value: AbsenceReason.infRS,
      },
      {
        text: '帯状疱疹',
        value: AbsenceReason.infZoster,
      },
      {
        text: '突発性発しん',
        value: AbsenceReason.infRoseola,
      },
      {
        text: '伝染性膿痂疹（とびひ）',
        value: AbsenceReason.infImpetigo,
      },
      {
        text: 'インフルエンザA型',
        value: AbsenceReason.infInfluenzaA,
      },
      {
        text: 'インフルエンザB型',
        value: AbsenceReason.infInfluenzaB,
      },
    ],
  }),

  methods: {
    close() {
      this.$emit('close');
    },
    cancelDatePick() {
      this.dateRangeText = '';
      this.dates = [];
      this.isDatePicker = false;
    },
    async clickPost() {
      if (this.form.validate()) {
        if (this.dates.length === 1) {
          // 1日だけ選択された場合も YYYY-MM-DD で統一
          this.startedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
          this.endedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
          console.log('1日だけ選択された場合', this.startedAt, this.endedAt);
        } else {
          const rangeFirstDate = this.dates[0]
          const rangeLastDate = this.dates.slice(-1)[0]; 
          this.dates = [rangeFirstDate,rangeLastDate]
          const d1 = dayjs(this.dates[0]);
          const d2 = dayjs(this.dates[1]);
          if (d1.isAfter(d2)) {
            this.startedAt = dayjs(this.dates[1]).format('YYYY-MM-DD');
            this.endedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
            console.log('d1.isAfter(d2) の場合', this.startedAt, this.endedAt);
          } else {
            this.startedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
            this.endedAt = dayjs(this.dates[1]).format('YYYY-MM-DD');
            console.log('d1.isBefore(d2) の場合', this.startedAt, this.endedAt);
          }
        }
        this.btnLoading = true;
        if (!this.comment) {
          this.comment = null;
        }
        try {
          if (this.reason === 'infection') {
            await chatModule.postChatsAbsence({
              childId: this.childId,
              startedAt: this.startedAt,
              endedAt: this.endedAt,
              reason: this.infectionReason,
            });
          } else {
            await chatModule.postChatsAbsence({
              childId: this.childId,
              startedAt: this.startedAt,
              endedAt: this.endedAt,
              reason: this.reason,
              comment: this.comment,
            });
          }

          this.$emit('posted');
          this.$emit('close');
          this.btnLoading = false;
        } catch (error) {
          this.$emit('close');
          this.btnLoading = false;
        }
      }
    },
  },

  async mounted() {
    this.fetchLoading = true;
    await chatModule.getChatsAbsence(this.childId),
      this.$nextTick(() => {
        this.fetchLoading = false;
      });
  },
});
</script>
<style lang="scss" scoped></style>
